import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import { ROLE_DASHBOARD_PATHS } from '../utils/constants';
import { storage } from '../utils/helpers';

// Layouts
import { AuthLayout } from '../layouts/AuthLayout';
import { DashboardLayout } from '../layouts/DashboardLayout';

// Route guard
import { ProtectedRoute } from './ProtectedRoute';

// Pages
import { Login } from '../pages/Login/Login';
import { ForgotPassword } from '../pages/ForgotPassword';
import { SuperAdminDashboard } from '../pages/Dashboard/SuperAdminDashboard';
import { PMODashboard } from '../pages/Dashboard/PMODashboard';
import { ManagerDashboard } from '../pages/Dashboard/ManagerDashboard';
import { EmployeeDashboard } from '../pages/Dashboard/EmployeeDashboard';
import { ITAdminDashboard } from '../pages/Dashboard/ITAdminDashboard';
import { Users } from '../pages/Users';
import { Roles } from '../pages/Roles';
import { Departments } from '../pages/Departments';
import { Clients } from '../pages/Clients';
import { Projects } from '../pages/Projects';
import { Allocations } from '../pages/Allocations';
import { LogTime } from '../pages/LogTime';
import { Timesheets } from '../pages/Timesheets';
import { Team } from '../pages/Team';
import { Approvals } from '../pages/Approvals';
import { Permissions } from '../pages/Permissions';
import { Holidays } from '../pages/Holidays';
import { Settings } from '../pages/Settings';
import { TaskTypes } from '../pages/TaskTypes';
import { Assets, AssetCategories, AssetStates, AssetLocations, AssetLogs, AssetAssignments } from '../pages/Assets';
import { AllTimesheets } from '../pages/AllTimesheets';
import { MyProjectTeam } from '../pages/MyProjectTeam';
import { WorkRequests } from '../pages/WorkRequests';
import { EmailNotifications } from '../pages/EmailNotifications';
import { AllProjects } from '../pages/AllProjects';
import { AllEmployees } from '../pages/AllEmployees';
import { DataImport } from '../pages/DataImport';
import { BenchEmployees } from '../pages/BenchEmployees';

// Component to redirect authenticated users to their dashboard
function AuthRedirect() {
  const { isAuthenticated, isLoading, user, userRole } = useAuth();
  const hasToken = !!storage.getToken();

  // If still loading and has token, wait (don't redirect to login yet)
  if (isLoading && hasToken) {
    return null; // Brief moment while auth initializes
  }

  if (isAuthenticated && user && userRole) {
    return <Navigate to={ROLE_DASHBOARD_PATHS[userRole]} replace />;
  }

  return <Navigate to="/login" replace />;
}

// Component to redirect to role-specific dashboard
function DashboardRedirect() {
  const { isLoading, user, userRole } = useAuth();
  const hasToken = !!storage.getToken();

  // If still loading and has token, wait
  if (isLoading && hasToken) {
    return null;
  }

  if (user && userRole) {
    return <Navigate to={ROLE_DASHBOARD_PATHS[userRole]} replace />;
  }

  return <Navigate to="/login" replace />;
}

export function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<AuthRedirect />} />

        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <AuthLayout>
              <Login />
            </AuthLayout>
          }
        />

        {/* Forgot Password */}
        <Route
          path="/forgot-password"
          element={
            <AuthLayout>
              <ForgotPassword />
            </AuthLayout>
          }
        />

        {/* Dashboard routes - protected */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Default dashboard redirect based on role */}
          <Route index element={<DashboardRedirect />} />

          {/* Super Admin Dashboard */}
          <Route
            path="admin"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* PMO Dashboard */}
          <Route
            path="pmo"
            element={
              <ProtectedRoute allowedRoles={[UserRole.PMO]}>
                <PMODashboard />
              </ProtectedRoute>
            }
          />

          {/* Manager Dashboard */}
          <Route
            path="manager"
            element={
              <ProtectedRoute allowedRoles={[UserRole.MANAGER]}>
                <ManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* Employee Dashboard */}
          <Route
            path="employee"
            element={
              <ProtectedRoute allowedRoles={[UserRole.EMPLOYEE]}>
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />

          {/* IT Admin Dashboard */}
          <Route
            path="it-admin"
            element={
              <ProtectedRoute allowedRoles={[UserRole.IT_ADMIN]}>
                <ITAdminDashboard />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Management Routes - Permission based */}
        <Route
          path="/employees"
          element={
            <ProtectedRoute requiredPermission="users.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Users />} />
        </Route>

        <Route
          path="/roles"
          element={
            <ProtectedRoute requiredPermission="roles.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Roles />} />
        </Route>

        <Route
          path="/departments"
          element={
            <ProtectedRoute requiredPermission="departments.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Departments />} />
        </Route>

        {/* Clients - Permission based */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute requiredPermission="clients.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Clients />} />
        </Route>

        {/* Projects - Permission based */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute anyPermission={['projects.view_all', 'projects.view_assigned']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Projects />} />
        </Route>

        {/* Allocations - Permission based */}
        <Route
          path="/allocations"
          element={
            <ProtectedRoute anyPermission={['projects.assign', 'requests.approve_project']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Allocations />} />
        </Route>

        {/* Log Time - Accessible to all authenticated users */}
        <Route
          path="/log-time"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<LogTime />} />
        </Route>

        {/* Timesheets - Accessible to all authenticated users */}
        <Route
          path="/timesheets"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Timesheets />} />
        </Route>

        {/* All Timesheets - PMO/SuperAdmin */}
        <Route
          path="/all-timesheets"
          element={
            <ProtectedRoute requiredPermission="timesheets.view_all">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AllTimesheets />} />
        </Route>

        {/* My Project's Team Timesheets - Manager */}
        <Route
          path="/my-project-team"
          element={
            <ProtectedRoute requiredPermission="timesheets.view_team">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyProjectTeam />} />
        </Route>

        {/* Team - Manager/PMO/SuperAdmin */}
        <Route
          path="/team"
          element={
            <ProtectedRoute requiredPermission="team.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Team />} />
        </Route>

        {/* Approvals - Manager/PMO/SuperAdmin */}
        <Route
          path="/approvals"
          element={
            <ProtectedRoute requiredPermission="timesheets.approve">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Approvals />} />
        </Route>

        {/* Permissions - SuperAdmin only */}
        <Route
          path="/permissions"
          element={
            <ProtectedRoute requiredPermission="users.manage_permissions">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Permissions />} />
        </Route>

        {/* Holidays - requires holidays.manage permission */}
        <Route
          path="/holidays"
          element={
            <ProtectedRoute requiredPermission="holidays.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Holidays />} />
        </Route>

        {/* Settings - requires settings.manage permission */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute requiredPermission="settings.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Settings />} />
        </Route>

        {/* Task Types - requires tasks.manage permission */}
        <Route
          path="/task-types"
          element={
            <ProtectedRoute requiredPermission="tasks.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TaskTypes />} />
        </Route>

        {/* Assets - requires assets.view permission */}
        <Route
          path="/assets"
          element={
            <ProtectedRoute requiredPermission="assets.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Assets />} />
        </Route>

        {/* Asset Assignments - requires assets.view permission */}
        <Route
          path="/assets/assignments"
          element={
            <ProtectedRoute requiredPermission="assets.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssetAssignments />} />
        </Route>

        {/* Asset Categories - requires asset_categories.manage permission */}
        <Route
          path="/assets/categories"
          element={
            <ProtectedRoute requiredPermission="asset_categories.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssetCategories />} />
        </Route>

        {/* Asset States - requires asset_states.manage permission */}
        <Route
          path="/assets/states"
          element={
            <ProtectedRoute requiredPermission="asset_states.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssetStates />} />
        </Route>

        {/* Asset Locations - requires asset_locations.manage permission */}
        <Route
          path="/assets/locations"
          element={
            <ProtectedRoute requiredPermission="asset_locations.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssetLocations />} />
        </Route>

        {/* Asset Logs - requires assets.view_logs permission */}
        <Route
          path="/assets/logs"
          element={
            <ProtectedRoute requiredPermission="assets.view_logs">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssetLogs />} />
        </Route>

        {/* Work Requests - employees/managers submit, PMO/managers resolve */}
        <Route
          path="/work-requests"
          element={
            <ProtectedRoute anyPermission={['requests.submit_work', 'requests.view_work']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<WorkRequests />} />
        </Route>


        {/* Email Notifications - SuperAdmin only */}
        <Route
          path="/email-notifications"
          element={
            <ProtectedRoute requiredPermission="settings.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<EmailNotifications />} />
        </Route>

        {/* All Projects Report - permission gated */}
        <Route
          path="/all-projects"
          element={
            <ProtectedRoute requiredPermission="reports.view_projects">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AllProjects />} />
        </Route>

        {/* All Employees Report - permission gated */}
        <Route
          path="/all-employees"
          element={
            <ProtectedRoute requiredPermission="reports.view_employees">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AllEmployees />} />
        </Route>

        {/* Bench Employees - PMO & SuperAdmin only */}
        <Route
          path="/bench-employees"
          element={
            <ProtectedRoute requiredPermission="bench.view">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BenchEmployees />} />
        </Route>

        {/* Data Import - SuperAdmin only */}
        <Route
          path="/data-import"
          element={
            <ProtectedRoute requiredPermission="import.manage">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DataImport />} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRoutes;
